/*
 * BLANC SPACE CUSTOM: Chatwoot live chat (website inbox 137956), the storefront's
 * only chat since Intercom was retired on 2026-09-17. First-page performance
 * contract: nothing on a visitor's first page in the tab, loaded during idle on
 * the second page, immediately for a support link (#chat, or the legacy
 * #intercom spelling still found in older content), in the theme editor and
 * under the ?bs_chat preview override. Configuration arrives from
 * snippets/support-chat.liquid as window.BlancSpaceSupportChatConfig. The CS
 * module (blancspace-os apps/cs) treats identity from the browser as
 * UNVERIFIED on purpose, so passing the logged-in customer here only saves a
 * pre-chat form; it never unlocks an order action. See AGENTS.md "Support chat".
 */
(() => {
  const SESSION_KEY = 'blancspace_chat_first_page_seen';
  const config = window.BlancSpaceSupportChatConfig || {};
  const chatwoot = config.chatwoot || {};

  if (config.activeProvider && config.activeProvider !== 'chatwoot') return;
  if (!chatwoot.websiteToken) return;

  const BASE_URL = String(chatwoot.baseUrl || 'https://app.chatwoot.com').replace(/\/$/, '');
  let loadPromise;
  let stateObserver;

  // Mirrors the SDK's open/closed state onto <html> so custom.css can lift the
  // open widget above theme chrome and keep the closed launcher beneath it.
  const setState = (isOpen) => {
    document.documentElement.dataset.bsChatState = isOpen ? 'open' : 'launcher';
  };

  const holder = () => document.querySelector('.woot-widget-holder');
  const holderIsOpen = () => {
    const element = holder();
    return Boolean(element) && !element.classList.contains('woot--hide');
  };

  const installStateController = (attempt = 0) => {
    if (stateObserver) return;
    const element = holder();

    if (!element) {
      if (attempt < 40) window.setTimeout(() => installStateController(attempt + 1), 250);
      return;
    }

    setState(holderIsOpen());
    stateObserver = new MutationObserver(() => setState(holderIsOpen()));
    stateObserver.observe(element, { attributes: true, attributeFilter: ['class'] });
  };

  const identify = () => {
    const sdk = window.$chatwoot;
    const customer = config.customer;
    if (!sdk) return;

    try {
      if (customer && customer.id) {
        sdk.setUser(`shopify-${customer.id}`, {
          email: customer.email || undefined,
          name: customer.name || undefined
        });
        sdk.setCustomAttributes({
          shopify_customer_id: String(customer.id),
          orders_count: Number(customer.ordersCount) || 0
        });
      }
    } catch (error) {
      // Identity is a convenience only; the chat still works anonymously.
    }
  };

  const waitForReady = () => new Promise((resolve) => {
    if (window.$chatwoot?.hasLoaded) {
      resolve(true);
      return;
    }

    let attempts = 0;
    let settled = false;
    const finish = (value) => {
      if (settled) return;
      settled = true;
      window.removeEventListener('chatwoot:ready', onReady);
      resolve(value);
    };
    const onReady = () => finish(true);
    const tick = () => {
      if (settled) return;
      if (window.$chatwoot?.hasLoaded) {
        finish(true);
        return;
      }
      if (attempts >= 80) {
        finish(false);
        return;
      }
      attempts += 1;
      window.setTimeout(tick, 250);
    };

    window.addEventListener('chatwoot:ready', onReady);
    tick();
  });

  const load = () => {
    if (loadPromise) return loadPromise;

    window.chatwootSettings = Object.assign({
      position: 'right',
      type: 'standard',
      locale: 'en',
      // The storefront is light-only (Blanc White); never let the widget follow
      // the phone's dark mode while the page behind it stays light.
      darkMode: 'light',
      hideMessageBubble: false,
      showPopoutButton: false,
      showUnreadMessagesDialog: false
    }, chatwoot.settings || {});

    loadPromise = new Promise((resolve, reject) => {
      const script = document.createElement('script');
      script.src = `${BASE_URL}/packs/js/sdk.js`;
      script.async = true;
      script.onload = () => {
        try {
          window.chatwootSDK.run({ websiteToken: chatwoot.websiteToken, baseUrl: BASE_URL });
          resolve();
        } catch (error) {
          reject(error);
        }
      };
      script.onerror = () => reject(new Error('Chatwoot SDK failed to load'));
      document.head.append(script);
    }).then(async () => {
      const isReady = await waitForReady();

      if (isReady) {
        installStateController();
        identify();
      }

      return isReady;
    });

    return loadPromise;
  };

  const open = async (fallbackUrl) => {
    try {
      const isReady = await load();

      if (!isReady || !window.$chatwoot) throw new Error('Chatwoot is unavailable');
      window.$chatwoot.toggle('open');
    } catch (error) {
      if (fallbackUrl) window.location.assign(fallbackUrl);
    }
  };

  window.BlancSpaceSupportChat = { provider: 'chatwoot', load, open };

  document.addEventListener('click', (event) => {
    if (!event.target.closest?.('a[href="/account/logout"]')) return;
    try {
      window.$chatwoot?.reset();
    } catch (error) {
      // Nothing to reset before the SDK has loaded.
    }
    setState(false);
  });

  // Record this page view before any early return. The theme editor and the
  // ?bs_chat preview override count as returning: both are someone checking the widget.
  let isReturningPage = Boolean(window.Shopify?.designMode) || Boolean(config.preview);

  try {
    isReturningPage ||= window.sessionStorage.getItem(SESSION_KEY) === 'true';
    window.sessionStorage.setItem(SESSION_KEY, 'true');
  } catch (error) {
    // Storage can be unavailable in privacy modes. Support links still load the chat on demand.
  }

  if (window.location.hash === '#chat' || window.location.hash === '#intercom') {
    open();
    return;
  }

  if (!isReturningPage) return;

  if ('requestIdleCallback' in window) {
    window.requestIdleCallback(load, { timeout: 4000 });
  } else {
    window.setTimeout(load, 1500);
  }
})();
